import useCreatePipeline from '@/api/mutations/pipelines/useCreatePipeline';
import useGetMyAccountPipelines from '@/api/queries/me/useGetMyAccountPipelines';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Field,
  Fieldset,
  Form,
  Modal,
  RawInput,
  Select,
} from '@payaca/ambrosia';
import { ACCOUNT_PIPELINES_LIMIT } from '@payaca/constants/pipeline';
import { FC, useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod/v4';

const FORM_SCHEMA = z.object({
  title: z
    .string()
    .min(1, 'Title is required')
    .max(255, 'Title must be less than 255 characters'),
  templatePipelineId: z.string().nullable(),
});

type FormType = z.infer<typeof FORM_SCHEMA>;

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (pipelinePublicId: string) => void;
  initialTemplatePipelineId?: string;
};

const AddPipelineModalV2: FC<Props> = ({
  isOpen,
  onClose,
  onSuccess,
  initialTemplatePipelineId,
}) => {
  const { data: pipelines } = useGetMyAccountPipelines({ enabled: isOpen });

  const hasReachedPipelineLimit = useMemo(() => {
    return (pipelines?.length ?? 0) >= ACCOUNT_PIPELINES_LIMIT;
  }, [pipelines]);

  const templateOptions = useMemo(() => {
    const options = [
      { label: 'Payaca default pipeline', value: '' },
      ...(pipelines ?? []).map((p) => ({
        label: p.title,
        value: p.publicId,
      })),
    ];
    return options;
  }, [pipelines]);

  const formMethods = useForm<FormType>({
    resolver: zodResolver(FORM_SCHEMA),
    mode: 'onChange',
    defaultValues: {
      title: '',
      templatePipelineId: initialTemplatePipelineId ?? null,
    },
  });

  const { mutateAsync: createPipeline, isPending } = useCreatePipeline();

  const handleSubmit = async (values: FormType) => {
    const result = await createPipeline({
      title: values.title,
      templatePipelineId: values.templatePipelineId || undefined,
    });

    onClose();
    onSuccess?.(result.createPipeline.publicId);
  };

  const handleClose = () => {
    formMethods.reset();
    onClose();
  };

  return (
    <Modal title="Add pipeline" isOpen={isOpen} onClose={handleClose}>
      {hasReachedPipelineLimit ? (
        <>
          <Modal.Body>
            <p>
              You have reached the limit of {ACCOUNT_PIPELINES_LIMIT} pipelines
              per account. Please remove an existing pipeline before adding a
              new one.
            </p>
          </Modal.Body>
          <Modal.Footer>
            <Modal.Footer.Actions>
              <Button variant="secondary" onClick={handleClose}>
                Close
              </Button>
            </Modal.Footer.Actions>
          </Modal.Footer>
        </>
      ) : (
        <Form methods={formMethods} onSubmit={handleSubmit}>
          <Modal.Body>
            <Fieldset>
              <Field name="title">
                <Field.Label>Title</Field.Label>
                <RawInput {...formMethods.register('title')} autoFocus />
              </Field>

              <Controller<FormType, 'templatePipelineId'>
                name="templatePipelineId"
                render={({ field: { value, onChange } }) => (
                  <Field name="templatePipelineId">
                    <Field.Label>
                      Which pipeline would you like to use as a template?
                    </Field.Label>
                    <Select
                      options={templateOptions}
                      value={value ?? ''}
                      onChange={(newValue) => onChange(newValue || null)}
                      multiple={false}
                      placeholder="Select a template"
                    />
                  </Field>
                )}
              />
            </Fieldset>
          </Modal.Body>

          <Modal.Footer>
            <Modal.Footer.Actions>
              <Button variant="secondary" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={!formMethods.formState.isValid}
                isProcessing={isPending}
              >
                Create
              </Button>
            </Modal.Footer.Actions>
          </Modal.Footer>
        </Form>
      )}
    </Modal>
  );
};

export default AddPipelineModalV2;
